import React, { useRef } from 'react';
import {
    useSsDispatch as useDispatch,
    useSsSelector as useSelector,
} from '~SpectateStore';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import {
    selectAvailableMultiSingles,
} from '~Betslip/selectors/availableBetTypesSelectors';
import {
    selectParlay,
    selectUserParlay,
    selectUserSinglesInParlay,
    selectPotentialReturnsMultiplier,
} from '~Betslip/selectors/parlaySelectors';
import {
    selectParlaySinglesTotalPayout,
    selectParlayParlayPayout,
} from '~Betslip/selectors/totalPayoutSelectors';
import {
    updateUserParlay,
    updateUserSingles,
} from '~Betslip/slices/parlayTabSlice';
import {
    selectCountSuspendedSelections,
    selectAnySelectionSuspended,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    useKeyboard,
} from '~Betslip/store/keyboardContext';

import {
    selectHasParlayExceedBalance,
} from '~Betslip/selectors/userSelectors';

import StakeField from '../StakeField';
import Payout from '../Payout';
import { getPriceOdds } from '~shared/components/sport-event/utils.js';

import {
    useIsEachwayAllowed,
    useOddsFormat,
} from '~SpectateStore/hooks/userHooks';

import './styles.scss';

const Parlay = () => {
    const dispatch = useDispatch();
    const [ t ] = useTranslation();
    const parlayKeyboardRef = useRef();
    const singlesKeyboardRef = useRef();
    const { setKeyboardContainerRef } = useKeyboard();
    const availableSingles = useSelector(selectAvailableMultiSingles);
    const parlay = useSelector(selectParlay);

    const userParlay = useSelector(selectUserParlay);
    const userSingles = useSelector(selectUserSinglesInParlay);
    const totalSuspended = useSelector(selectCountSuspendedSelections);
    const anySelectionSuspended = useSelector(selectAnySelectionSuspended);

    const parlaySinglesPayout = useSelector(selectParlaySinglesTotalPayout);
    const parlayPayout = useSelector(selectParlayParlayPayout);
    const potentialReturnsMultiplier = useSelector(selectPotentialReturnsMultiplier);
    const eachWayAllowed = useIsEachwayAllowed();
    const { isDepositNeeded } = useSelector(selectHasParlayExceedBalance);
    const oddsFormat = useOddsFormat();

    const updateUserParlayChoices = (stake, each_way_selected, free_bet_id) => {
        const fbStake = (free_bet_id) ? 0 : stake;
        dispatch(updateUserParlay({
            stake: fbStake,
            each_way_selected,
            free_bet_id,
        }));
    };

    const updateUserSinglesChoices = (stake, each_way_selected) => {
        dispatch(updateUserSingles({
            stake,
            each_way_selected,
        }));
    };

    const openKeyboard = (ref) => {
        setKeyboardContainerRef(ref.current);
    };

    const renderParlay = () => {
        const parlayIsDisabled = !parlay || anySelectionSuspended;
        const ewAllowed = (parlay?.each_way_allowed && eachWayAllowed) || false;

        const {
            stake,
            each_way_selected,
            free_bet_id,
        } = userParlay;

        const payout = parlayIsDisabled ? 0 : parlayPayout;
        const betslipId = parlay?.id;

        const betTypeClasses = classNames(
            'fullslip__bet-type',
            {
                'fullslip__bet-type--disabled': parlayIsDisabled,
            },
        );

        const gtmDetails = {
            source: 'new-betslip',
            betType: betslipId,
        };

        const parlayName = () => {
            const betName = parlay?.name;
            if (!betName) {
                return t('betslip.parlay');
            }
            const specialCases = ['Doubles', 'Trebles'];
            if (!specialCases.includes(betName)) {
                return t(`bet_types.name.${betName}`);
            }

            return t(`betslip.acca_${betName}`);
        };

        return (
            <li
                className={betTypeClasses}
                data-test-id="betslip;parlay-parlay"
            >
                <div
                    className="fullslip__bet-type-details"
                    data-test-id="betslip;bet-type-details"
                >
                    <div
                        className="fullslip__bet-name"
                        data-test-id="betslip;bet-type-name"
                    >
                        {parlayName()}
                    </div>
                    {
                        parlayIsDisabled
                            ? null
                            : <div className="fullslip__parlay-odds">
                                {
                                    potentialReturnsMultiplier
                                    ? `@ ${getPriceOdds(potentialReturnsMultiplier, oddsFormat, 2)}`
                                    : t('betslip.sp')
                                }
                            </div>
                    }
                    <StakeField
                        stake={stake}
                        each_way_allowed={ewAllowed}
                        each_way_selected={each_way_selected}
                        onChangeCallback={updateUserParlayChoices}
                        disabled={parlayIsDisabled}
                        onClickHandler={() => openKeyboard(parlayKeyboardRef)}
                        freebetId={free_bet_id}
                        betslipId={betslipId}
                        gtmDetails={gtmDetails}
                        depositRequired={isDepositNeeded}
                    />
                    <Payout payout={payout} />
                </div>
                <div ref={parlayKeyboardRef} />
            </li>
        );
    };

    const renderAvailableSingles = () => {
        if (!availableSingles) {
            return null;
        }

        const {
            name,
            total,
            each_way_allowed,
        } = availableSingles;

        const {
            stake,
            each_way_selected,
        } = userSingles;


        const totalAvailableSingles = total - totalSuspended;

        const gtmDetails = {
            source: 'new-betslip',
            betType: 'singles',
        };

        return (
            <li
                className="fullslip__bet-type"
                data-test-id="betslip;parlay-singles"
            >
                <div
                    className="fullslip__bet-type-details"
                    data-test-id="betslip;bet-type-details"
                >
                    <div
                        className="fullslip__bet-name"
                        data-test-id="betslip;bet-type-name"
                    >
                        {t(`bet_types.name.${name}`)} (x{totalAvailableSingles})
                    </div>
                    <div className="fullslip__wager-text">
                        {t('betslip.wager_per_bet')}
                    </div>
                    <StakeField
                        stake={stake}
                        each_way_allowed={each_way_allowed && eachWayAllowed}
                        each_way_selected={each_way_selected}
                        onChangeCallback={updateUserSinglesChoices}
                        onClickHandler={() => openKeyboard(singlesKeyboardRef)}
                        gtmDetails={gtmDetails}
                        depositRequired={isDepositNeeded}
                    />
                    <Payout payout={parlaySinglesPayout} />
                </div>
                <div ref={singlesKeyboardRef} />
            </li>
        );
    };

    return (
        <ul className="fullslip__bet-types">
            {renderParlay()}
            {renderAvailableSingles()}
        </ul>
    );
};

export default Parlay;
